import { useEffect, useMemo, useState } from "react";
import type { BarConfig, LineConfig } from "../../../components/Charts";
import chartDataFetcher, { type P5RawDataPoint } from "../../../services/api/ChartDataFetch";
import {
  CHART_DIMENSION_PALETTE,
  CHART_SEMANTIC_CORRECT,
  CHART_SEMANTIC_INCORRECT,
  CHART_SEMANTIC_MODERATE,
  chartDimensionColorAt,
} from "../../../constants/chartColors";

export interface P5FilterValues {
  comarques?: string[];
  sector?: string;
}

export interface P5ComarcaSectorPoint extends Record<string, string | number> {
  nom_comarca: string;
}

export interface P5SectorComarcaPoint extends Record<string, string | number> {
  nom_comarca: string;
  iaup: number;
}

export interface P5OfertaComarcalRow {
  nom_comarca: string;
  teOferta: boolean;
  sectorsAmbOferta: string[];
  avgIaup: number | null;
  avgDelta: number | null;
}

interface P5HeatmapSummary {
  topSector: {
    label: string;
    avgIaup: number;
    avgDelta: number;
  };
  topComarca: {
    label: string;
    avgIaup: number;
    avgDelta: number;
  };
  topCell: {
    sector: string;
    comarca: string;
    value: number;
    delta: number;
  };
}

const IAUP_REFERENCE = 1;
const IAUP_TOLERANCE = 0.1;
const IAUP_MODERATE_DELTA = 0.25;
function getIaupDeviation(value: number) {
  return Math.abs(value - IAUP_REFERENCE);
}

function getIaupLevelColor(value: number) {
  const deviation = getIaupDeviation(value);
  if (deviation <= IAUP_TOLERANCE) return CHART_SEMANTIC_CORRECT;
  if (deviation <= IAUP_MODERATE_DELTA) return CHART_SEMANTIC_MODERATE;
  return CHART_SEMANTIC_INCORRECT;
}

function formatSignedDelta(value: number) {
  const rounded = Number(value.toFixed(2));
  const formatted = Number.isInteger(rounded)
    ? rounded.toString()
    : rounded.toFixed(1).replace(/\.0$/, "");

  return `${rounded > 0 ? "+" : ""}${formatted}`;
}

function getDefaultComarques(rawData: P5RawDataPoint[]): string[] {
  return Array.from(new Set(rawData.map((row) => row.nom_comarca))).sort();
}

function buildSectorLinesByComarca(
  rawData: P5RawDataPoint[],
  selectedComarques: string[]
): {
  chartData: P5ComarcaSectorPoint[];
  lines: LineConfig[];
} {
  const comarques = selectedComarques.filter((comarca) =>
    rawData.some((row) => row.nom_comarca === comarca)
  ).sort((a, b) => {
    const aRows = rawData.filter((row) => row.nom_comarca === a);
    const bRows = rawData.filter((row) => row.nom_comarca === b);
    const aAvg = aRows.reduce((sum, row) => sum + row.iaup, 0) / aRows.length;
    const bAvg = bRows.reduce((sum, row) => sum + row.iaup, 0) / bRows.length;
    return bAvg - aAvg;
  });
  const sectors = Array.from(new Set(rawData.map((d) => d.nom_sector))).sort();

  const chartData: P5ComarcaSectorPoint[] = comarques.map((comarca) => {
    const point: P5ComarcaSectorPoint = { nom_comarca: comarca };
    const rows = rawData.filter((d) => d.nom_comarca === comarca);
    sectors.forEach((sector) => {
      const row = rows.find((d) => d.nom_sector === sector);
      if (row) point[sector] = row.iaup;
    });
    return point;
  });

  const lines: LineConfig[] = sectors.map((sector, index) => {
    const dimColor = chartDimensionColorAt(index);
    return {
      key: sector,
      label: sector,
      color: dimColor,
      pointColor: dimColor,
    };
  });

  return { chartData, lines };
}

function buildEnrichedHeatmapByComarca(
  rawData: P5RawDataPoint[],
  selectedComarques: string[]
): {
  chartData: P5ComarcaSectorPoint[];
  series: LineConfig[];
  rowMeta: Record<string, string>;
  columnMeta: Record<string, string>;
  summary: P5HeatmapSummary | null;
} {
  const comarques = selectedComarques.filter((comarca) =>
    rawData.some((row) => row.nom_comarca === comarca)
  );
  const selectedRows = rawData.filter((row) => comarques.includes(row.nom_comarca));
  const sectors = Array.from(new Set(selectedRows.map((d) => d.nom_sector)));

  const comarcaStats = comarques.map((comarca) => {
    const rows = selectedRows.filter((row) => row.nom_comarca === comarca);
    const avgIaup = rows.reduce((sum, row) => sum + row.iaup, 0) / rows.length;

    return {
      comarca,
      avgIaup,
      avgDelta: avgIaup - IAUP_REFERENCE,
      avgDeviation: getIaupDeviation(avgIaup),
    };
  }).sort((a, b) => {
    const iAupDiff = b.avgIaup - a.avgIaup;
    if (iAupDiff !== 0) return iAupDiff;
    const deviationDiff = b.avgDeviation - a.avgDeviation;
    if (deviationDiff !== 0) return deviationDiff;
    return a.comarca.localeCompare(b.comarca);
  });

  const sectorStats = sectors.map((sector) => {
    const rows = selectedRows.filter((row) => row.nom_sector === sector);
    const avgIaup = rows.reduce((sum, row) => sum + row.iaup, 0) / rows.length;

    return {
      sector,
      avgIaup,
      avgDelta: avgIaup - IAUP_REFERENCE,
      avgDeviation: getIaupDeviation(avgIaup),
    };
  }).sort((a, b) => {
    const deviationDiff = b.avgDeviation - a.avgDeviation;
    if (deviationDiff !== 0) return deviationDiff;
    return a.sector.localeCompare(b.sector);
  });

  const orderedComarques = comarcaStats.map((item) => item.comarca);
  const orderedSectors = sectorStats.map((item) => item.sector);

  const chartData: P5ComarcaSectorPoint[] = orderedComarques.map((comarca) => {
    const point: P5ComarcaSectorPoint = { nom_comarca: comarca };
    const rows = rawData.filter((d) => d.nom_comarca === comarca);

    orderedSectors.forEach((sector) => {
      const row = rows.find((d) => d.nom_sector === sector);
      if (row) point[sector] = row.iaup;
    });

    return point;
  });

  const rowMeta = Object.fromEntries(
    sectorStats.map(({ sector, avgIaup, avgDelta }) => [
      sector,
      `Mitjana ${avgIaup.toFixed(2)} · Δ ${formatSignedDelta(avgDelta)}`,
    ])
  );

  const columnMeta = Object.fromEntries(
    comarcaStats.map(({ comarca, avgIaup, avgDelta }) => [
      comarca,
      `Mitjana ${avgIaup.toFixed(2)} · Δ ${formatSignedDelta(avgDelta)}`,
    ])
  );

  const topCell = rawData
    .filter((row) => orderedComarques.includes(row.nom_comarca))
    .reduce<{ sector: string; comarca: string; value: number; delta: number } | null>((current, row) => {
      const candidate = {
        sector: row.nom_sector,
        comarca: row.nom_comarca,
        value: row.iaup,
        delta: row.iaup - IAUP_REFERENCE,
      };

      if (!current) return candidate;
      return Math.abs(candidate.delta) > Math.abs(current.delta) ? candidate : current;
    }, null);

  const summary = sectorStats.length > 0 && comarcaStats.length > 0 && topCell
    ? {
        topSector: {
          label: sectorStats[0].sector,
          avgIaup: Number(sectorStats[0].avgIaup.toFixed(2)),
          avgDelta: Number(sectorStats[0].avgDelta.toFixed(2)),
        },
        topComarca: {
          label: comarcaStats[0].comarca,
          avgIaup: Number(comarcaStats[0].avgIaup.toFixed(2)),
          avgDelta: Number(comarcaStats[0].avgDelta.toFixed(2)),
        },
        topCell: {
          ...topCell,
          value: Number(topCell.value.toFixed(2)),
          delta: Number(topCell.delta.toFixed(2)),
        },
      }
    : null;

  return {
    chartData,
    series: orderedSectors.map((sector, index) => ({
      key: sector,
      label: sector,
      color: chartDimensionColorAt(index),
    })),
    rowMeta,
    columnMeta,
    summary,
  };
}

function buildComarcaBarsBySector(
  rawData: P5RawDataPoint[],
  selectedSector?: string
): {
  chartData: P5SectorComarcaPoint[];
  bars: BarConfig[];
  sectorMean: number;
} {
  if (!selectedSector) {
    return { chartData: [], bars: [], sectorMean: IAUP_REFERENCE };
  }

  const chartData = rawData
    .filter((row) => row.nom_sector === selectedSector)
    .map((row) => ({
      nom_comarca: row.nom_comarca,
      iaup: row.iaup,
    }))
    .sort((a, b) => b.iaup - a.iaup);

  const sectorMean = chartData.length
    ? Number(
        (
          chartData.reduce((sum, row) => sum + row.iaup, 0) /
          chartData.length
        ).toFixed(2)
      )
    : IAUP_REFERENCE;

  return {
    chartData,
    bars: [
      {
        key: "iaup",
        label: selectedSector,
        color: CHART_DIMENSION_PALETTE[0],
      },
    ],
    sectorMean,
  };
}

export function useP5Data(filters: P5FilterValues) {
  const [rawData, setRawData] = useState<P5RawDataPoint[]>(
    () => chartDataFetcher.getCachedSync<P5RawDataPoint[]>("p5") ?? []
  );
  const [loading, setLoading] = useState(
    () => chartDataFetcher.getCachedSync<P5RawDataPoint[]>("p5") === null
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (chartDataFetcher.getCachedSync("p5") !== null) return;

    let cancelled = false;

    setLoading(true);
    setError(null);

    chartDataFetcher
      .getP5Data()
      .then((data) => { if (!cancelled) setRawData(data); })
      .catch((e: unknown) => {
        if (!cancelled)
          setError(e instanceof Error ? e.message : "Error carregant dades");
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, []);

  const comarquesWithOfertaSet = useMemo(() => {
    return new Set(
      rawData
        .filter((row) => Number(row.places_sector) > 0)
        .map((row) => row.nom_comarca)
    );
  }, [rawData]);

  const filteredRawData = useMemo(
    () => rawData.filter((row) => comarquesWithOfertaSet.has(row.nom_comarca)),
    [rawData, comarquesWithOfertaSet]
  );

  const availableComarques = useMemo(
    () => Array.from(new Set(filteredRawData.map((d) => d.nom_comarca))).sort(),
    [filteredRawData]
  );

  const availableSectors = useMemo(
    () => Array.from(new Set(filteredRawData.map((d) => d.nom_sector))).sort(),
    [filteredRawData]
  );

  const defaultComarques = useMemo(
    () => getDefaultComarques(filteredRawData),
    [filteredRawData]
  );

  const ofertaComarcalRows = useMemo<P5OfertaComarcalRow[]>(() => {
    const sectorsByComarca = new Map<string, Set<string>>();
    const iaupValuesByComarca = new Map<string, number[]>();

    rawData.forEach((row) => {
      if (!sectorsByComarca.has(row.nom_comarca)) {
        sectorsByComarca.set(row.nom_comarca, new Set());
      }
      if (!iaupValuesByComarca.has(row.nom_comarca)) {
        iaupValuesByComarca.set(row.nom_comarca, []);
      }
      iaupValuesByComarca.get(row.nom_comarca)?.push(row.iaup);
      if (Number(row.places_sector) > 0) {
        sectorsByComarca.get(row.nom_comarca)?.add(row.nom_sector);
      }
    });

    return Array.from(sectorsByComarca.entries())
      .map(([nom_comarca, sectors]) => {
        const values = iaupValuesByComarca.get(nom_comarca) ?? [];
        const avgIaup = values.length > 0
          ? Number((values.reduce((sum, value) => sum + value, 0) / values.length).toFixed(2))
          : null;
        const avgDelta = avgIaup === null ? null : Number((avgIaup - IAUP_REFERENCE).toFixed(2));

        return {
          nom_comarca,
          teOferta: sectors.size > 0,
          sectorsAmbOferta: Array.from(sectors).sort(),
          avgIaup,
          avgDelta,
        };
      })
      .sort((a, b) => {
        if (a.teOferta !== b.teOferta) return a.teOferta ? -1 : 1;
        const aAvg = a.avgIaup ?? -Infinity;
        const bAvg = b.avgIaup ?? -Infinity;
        if (bAvg !== aAvg) return bAvg - aAvg;
        return a.nom_comarca.localeCompare(b.nom_comarca);
      });
  }, [rawData]);

  const selectedComarques = useMemo(() => {
    if (filters.comarques !== undefined) {
      return filters.comarques.filter((comarca) => comarquesWithOfertaSet.has(comarca));
    }
    return defaultComarques;
  }, [filters.comarques, defaultComarques, comarquesWithOfertaSet]);

  const selectedSector = useMemo(() => {
    if (filters.sector) return filters.sector;
    return availableSectors.find((sector) => sector.toLocaleLowerCase() === "serveis")
      ?? availableSectors[0];
  }, [filters.sector, availableSectors]);

  const chart1 = useMemo(() => {
    return buildSectorLinesByComarca(filteredRawData, selectedComarques);
  }, [filteredRawData, selectedComarques]);

  const heatmapChart = useMemo(() => {
    return buildEnrichedHeatmapByComarca(filteredRawData, selectedComarques);
  }, [filteredRawData, selectedComarques]);

  const chart2 = useMemo(() => {
    return buildComarcaBarsBySector(filteredRawData, selectedSector);
  }, [filteredRawData, selectedSector]);

  const chart2Domain = useMemo(() => {
    const maxValue = Math.max(
      IAUP_REFERENCE,
      chart2.sectorMean,
      ...chart2.chartData.map((row) => row.iaup)
    );

    return [0, Number((maxValue * 1.15).toFixed(2))] as [number, number];
  }, [chart2.chartData, chart2.sectorMean]);

  const lineChartDomain = useMemo(() => {
    const values = chart1.chartData.flatMap((point) =>
      Object.entries(point)
        .filter(([key, value]) => key !== "nom_comarca" && typeof value === "number")
        .map(([, value]) => value as number)
    );

    if (values.length === 0) return [0.4, 1.6] as [number, number];

    const minValue = Math.min(IAUP_REFERENCE, ...values);
    const maxValue = Math.max(IAUP_REFERENCE, ...values);
    const padding = 0.15;

    return [
      Math.max(0, Number((minValue - padding).toFixed(2))),
      Number((maxValue + padding).toFixed(2)),
    ] as [number, number];
  }, [chart1.chartData]);

  return {
    chart1Data: chart1.chartData,
    chart1Lines: chart1.lines,
    heatmapData: heatmapChart.chartData,
    heatmapSeries: heatmapChart.series,
    heatmapRowMeta: heatmapChart.rowMeta,
    heatmapColumnMeta: heatmapChart.columnMeta,
    heatmapSummary: heatmapChart.summary,
    chart2Data: chart2.chartData,
    chart2Bars: chart2.bars,
    chart2Mean: chart2.sectorMean,
    chart2Domain,
    lineChartDomain,
    availableComarques,
    availableSectors,
    defaultComarques,
    ofertaComarcalRows,
    selectedSector,
    getIaupLevelColor,
    loading,
    error,
  };
}
